"use client";

import { useEffect, useRef } from "react";

// Process-flow hero art: particles travel left -> right through five stage
// gates, each crossing emitting a soft pulse. The delivery process as a
// living diagram. (See plans/001-hero-process-flow-canvas.md.)
const GATES = [0.14, 0.32, 0.5, 0.68, 0.86];
const PARTICLES = 7;
const PULSE_MS = 600;
const HIT_MS = 400;

type Particle = {
  x: number;
  v: number; // px per ms, set at spawn
  phase: number;
  lead: boolean;
};

type Pulse = { gate: number; t0: number };

export default function ProcessCanvas() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let W = 0;
    let H = 0;
    let dpr = 1;
    let raf = 0;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let mx = 0;
    let my = 0;
    let targetMx = 0;
    let targetMy = 0;

    const particles: Particle[] = [];
    const pulses: Pulse[] = [];
    const gateHits: number[] = GATES.map(() => -Infinity);
    let spawnCount = 0;

    const spawnVelocity = () => (W / 7000) * (0.85 + Math.random() * 0.3);

    const initParticles = () => {
      particles.length = 0;
      if (reduceMotion) {
        // static diagram: three resting particles between the gates
        [0.25, 0.5, 0.75].forEach((f, i) =>
          particles.push({ x: f * W, v: 0, phase: i * 1.7, lead: i === 1 })
        );
        return;
      }
      for (let i = 0; i < PARTICLES; i++) {
        particles.push({
          x: (i / PARTICLES) * W,
          v: spawnVelocity(),
          phase: i * 1.7,
          lead: i === 0,
        });
      }
    };

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = entry.contentRect.width;
      H = entry.contentRect.height;

      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);

      if (targetMx === 0 && targetMy === 0) {
        mx = W / 2;
        my = H / 2;
        targetMx = W / 2;
        targetMy = H / 2;
      }
      if (particles.length === 0 || reduceMotion) initParticles();
      // reduced motion: render a single static frame at the new size
      if (reduceMotion) raf = requestAnimationFrame(loop);
    });
    ro.observe(canvas);

    const onMove = (e: MouseEvent) => {
      targetMx = e.offsetX;
      targetMy = e.offsetY;
    };
    const onLeave = () => {
      targetMx = W / 2;
      targetMy = H / 2;
    };
    canvas.addEventListener("mousemove", onMove, { passive: true });
    canvas.addEventListener("mouseleave", onLeave, { passive: true });

    const colors = () => {
      const dark = document.documentElement.getAttribute("data-theme") === "dark";
      return {
        a: dark ? "106,141,255" : "43,95,255",
        fg: dark ? "243,243,240" : "23,23,26",
      };
    };

    let last = 0;

    function drawScene(now: number, dt: number) {
      const c = colors();
      ctx!.clearRect(0, 0, W, H);
      mx += (targetMx - mx) * 0.06;
      my += (targetMy - my) * 0.06;
      const midY = H * 0.5;
      const dy = (my - H / 2) * 0.08;

      // faint grid
      ctx!.strokeStyle = `rgba(${c.fg},0.05)`;
      ctx!.lineWidth = 1;
      const step = W / 10;
      for (let x = step; x < W; x += step) {
        ctx!.beginPath();
        ctx!.moveTo(x, 0);
        ctx!.lineTo(x, H);
        ctx!.stroke();
      }
      for (let y = step; y < H; y += step) {
        ctx!.beginPath();
        ctx!.moveTo(0, y);
        ctx!.lineTo(W, y);
        ctx!.stroke();
      }

      // baseline
      ctx!.strokeStyle = `rgba(${c.fg},0.08)`;
      ctx!.beginPath();
      ctx!.moveTo(0, midY);
      ctx!.lineTo(W, midY);
      ctx!.stroke();

      // gates
      GATES.forEach((g, gi) => {
        const gx = g * W;
        const hitAge = now - gateHits[gi];
        const hit = Math.max(0, 1 - hitAge / HIT_MS);
        const nearCursor = 0.2 * Math.exp(-((gx - mx) ** 2) / (2 * 60 ** 2));
        const alpha = 0.18 + 0.32 * hit + nearCursor;
        ctx!.strokeStyle = hit > 0 ? `rgba(${c.a},${alpha})` : `rgba(${c.fg},${alpha})`;
        ctx!.lineWidth = 1;
        ctx!.beginPath();
        ctx!.arc(gx, midY, 10, 0, Math.PI * 2);
        ctx!.stroke();
      });

      // pulses
      for (let i = pulses.length - 1; i >= 0; i--) {
        const t = (now - pulses[i].t0) / PULSE_MS;
        if (t >= 1) {
          pulses.splice(i, 1);
          continue;
        }
        const gx = GATES[pulses[i].gate] * W;
        ctx!.strokeStyle = `rgba(${c.a},${0.5 * (1 - t)})`;
        ctx!.lineWidth = 1.5 - t;
        ctx!.beginPath();
        ctx!.arc(gx, midY, 10 + 16 * t, 0, Math.PI * 2);
        ctx!.stroke();
      }

      // particles
      particles.forEach((pt) => {
        const prevX = pt.x;
        pt.x += pt.v * dt;
        GATES.forEach((g, gi) => {
          const gx = g * W;
          if (prevX < gx && pt.x >= gx) {
            pulses.push({ gate: gi, t0: now });
            gateHits[gi] = now;
          }
        });
        if (pt.x > W + 10) {
          spawnCount++;
          pt.x = -10;
          pt.v = spawnVelocity();
          pt.phase = Math.random() * Math.PI * 2;
          pt.lead = spawnCount % 4 === 0;
        }
        const edge = Math.max(0, Math.min(1, pt.x / (W * 0.08), (W - pt.x) / (W * 0.08)));
        const alpha = (pt.lead ? 0.95 : 0.55) * edge;
        const y = midY + dy + Math.sin(pt.x * 0.02 + pt.phase) * 6;
        ctx!.fillStyle = `rgba(${c.a},${alpha})`;
        ctx!.beginPath();
        ctx!.arc(pt.x, y, pt.lead ? 3.5 : 2.5, 0, Math.PI * 2);
        ctx!.fill();
      });
    }

    function loop(t: number) {
      const dt = last ? Math.min(t - last, 50) : 16;
      last = t;
      drawScene(t, reduceMotion ? 0 : dt);
      if (!reduceMotion) raf = requestAnimationFrame(loop);
    }
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return <canvas ref={ref} id="processCanvas" />;
}
